import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiFieldComponent,
  QaiLinkButtonComponent,
  QaiTreeSelectComponent,
  SelectedOptions,
} from '@qai/ui';
import { IUserAssignments } from 'src/app/interfaces/self-serve/assignment-manager/iuser-assignments';
import {
  IParsedTeam,
  IParsedUser,
} from 'src/app/interfaces/self-serve/assignment-manager/iteams-with-users-parser';
import { IUserWithTeam } from 'src/app/interfaces/self-serve/assignment-manager/requests/iusers-assignments';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { TeamUserParserService } from '../../../services/team-user-parser.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { IUsersWithTeams } from 'src/app/interfaces/self-serve/assignment-manager/responses/iusers-with-teams';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'add-users-to-assignment-modal',
  standalone: true,
  templateUrl: './add-users-to-assignment-modal.component.html',
  styleUrls: ['./add-users-to-assignment-modal.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    QaiFieldComponent,
    QaiDialogComponent,
    QaiFieldComponent,
    QaiButtonComponent,
    QaiLinkButtonComponent,
    QaiTreeSelectComponent,
  ],
})
export class AddUserToAssignmentModalComponent {
  @ViewChild('dialog') qaiDialogRef: QaiDialogComponent | undefined;
  @ViewChild(QaiTreeSelectComponent)
  treeSelectComponent!: QaiTreeSelectComponent;

  @Input() open: boolean = false;
  @Input() allTeamsWithUsers: IParsedTeam[] = [];
  @Input() networkRequestInProgress: boolean = false;
  @Input() currentAssignees: IUserAssignments[] = [];
  @Input() moduleAssignmentUuid: string = '';
  @Input() isCreateAssignmentFlow: boolean = false;

  @Output() addAssignmentClicked = new EventEmitter<string>();
  @Output() closeModal = new EventEmitter<void>();
  @Output() addToAssignmentClicked = new EventEmitter<IUserWithTeam[]>();

  selectedUsers: IUserWithTeam[] = [];

  constructor(
    private assignmentsService: AssignmentsService,
    public teamUserParserService: TeamUserParserService,
    private route: ActivatedRoute
  ) {}

  showError: boolean = false;
  dialogDescription: string =
    'Select the users that will participate in the assignment. Searching for a team will allow you to assign or unassign specific users on that team. When selecting a specific user, make sure they are associated with the correct team for reporting purposes.';

  searchFieldDescription: string =
    'Type at least three characters to begin searching';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      const isOpenNow = changes['open']?.currentValue;

      if (isOpenNow) {
        this.openModal();
        this.networkRequestInProgress = false;
      } else {
        if (this.qaiDialogRef) {
          this.qaiDialogRef.close();
        }
      }
    }

    this.getAllUsersWithTeams();
  }

  handleSuccessClick() {
    this.networkRequestInProgress = true;
    this.addToAssignment(this.selectedUsers);
  }

  handleCloseClick() {
    this.closeModal.emit();
  }

  openModal() {
    this.selectedUsers = [];

    if (this.treeSelectComponent) {
      this.treeSelectComponent.deselectAll();
      this.treeSelectComponent.searchTerm = '';
      this.treeSelectComponent.filterTreeData();
    }

    if (this.qaiDialogRef) {
      this.qaiDialogRef.showModal();
    }
  }

  onSelectionSave(data: SelectedOptions[]) {
    const parsedArray = (data as unknown as IParsedUser[]).map((user) => ({
      userUuid: user.uuid,
      teamUuid: user.teamUuid,
    }));

    this.selectedUsers = parsedArray;
  }

  get modalHeading(): string {
    return this.isCreateAssignmentFlow
      ? 'Create new assignment'
      : 'Add users to the assignment';
  }

  get modalButtonText(): string {
    return this.isCreateAssignmentFlow
      ? 'Save assignment'
      : 'Add to assignment';
  }

  get fieldLabel(): string {
    const baseLabel = 'Find users';
    return this.selectedUsers.length > 0
      ? `${baseLabel} (${this.selectedUsers.length} selected)`
      : baseLabel;
  }

  onRemoveAll() {
    this.selectedUsers = [];
    if (this.treeSelectComponent) {
      this.treeSelectComponent.deselectAll();
    }
  }

  getAllUsersWithTeams(): any {
    const storedTeamsWithUsers =
      SelfServeLocalStorageUtil.getTransformedTeamsWithUsers();
    if (storedTeamsWithUsers.length !== 0) {
      this.allTeamsWithUsers =
        this.teamUserParserService.markAssignedUsersOnParsedTeams(
          storedTeamsWithUsers,
          this.currentAssignees
        );

      return;
    }

    this.assignmentsService.getAllUsersWithTeams().subscribe({
      next: (usersWithTeams: IUsersWithTeams) => {
        this.allTeamsWithUsers =
          this.teamUserParserService.transformTeamUserData(usersWithTeams);

        this.allTeamsWithUsers =
          this.teamUserParserService.markAssignedUsersOnParsedTeams(
            this.allTeamsWithUsers,
            this.currentAssignees
          );

        SelfServeLocalStorageUtil.setTransformedTeamsWithUsers(
          JSON.stringify(this.allTeamsWithUsers)
        );
      },
      error: (err: any) => {
        console.error('Failed to users with teams', err);
      },
    });
  }

  addToAssignment(usersWithTeams: IUserWithTeam[]) {
    this.assignmentsService
      .assignUsersWithTeamContext(this.moduleAssignmentUuid, usersWithTeams)
      .subscribe({
        next: () => {
          this.addToAssignmentClicked.emit(this.selectedUsers);
        },
        error: (err: any) => {
          console.error('Failed to users with teams', err);
        },
      });
  }
}
